// Файл docx_preview_normalizer.js

import JSZip from 'jszip';



const word_namespace = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
const word_files_pattern = /^word\/(document|header\d+|footer\d+|footnotes|endnotes)\.xml$/;

const word_attribute = (element, name) => (
    element?.getAttributeNS(word_namespace, name)
    || element?.getAttribute(`w:${name}`)
    || ''
);

const first_word_element = (parent, local_name) => (
    parent?.getElementsByTagNameNS(word_namespace, local_name)?.[0]
    || null
);

const direct_word_child = (parent, local_name) => {
    if (!parent) {
        return null;
    }

    return [...parent.children].find((child) => (
        child.namespaceURI === word_namespace
        && child.localName === local_name
    )) || null;
};

const create_word_element = (document_xml, local_name) => (
    document_xml.createElementNS(
        word_namespace,
        `w:${local_name}`
    )
);

const read_font = (run_properties) => {
    const fonts = direct_word_child(run_properties, 'rFonts');

    if (!fonts) {
        return '';
    }

    return word_attribute(fonts, 'ascii')
        || word_attribute(fonts, 'hAnsi')
        || word_attribute(fonts, 'cs')
        || word_attribute(fonts, 'eastAsia')
        || '';
};

const read_alignment = (paragraph_properties) => {
    const alignment = direct_word_child(paragraph_properties, 'jc');

    return word_attribute(alignment, 'val');
};

const parse_styles = (styles_text) => {
    const document_xml = new DOMParser().parseFromString(
        styles_text,
        'application/xml'
    );
    const styles = {};
    let default_style_id = '';

    for (const style of document_xml.getElementsByTagNameNS(
        word_namespace,
        'style'
    )) {
        const style_id = word_attribute(style, 'styleId');

        if (!style_id) {
            continue;
        }

        const style_type = word_attribute(style, 'type');
        const based_on = word_attribute(
            direct_word_child(style, 'basedOn'),
            'val'
        );
        const name = word_attribute(
            direct_word_child(style, 'name'),
            'val'
        );
        styles[style_id] = {
            based_on,
            font: read_font(direct_word_child(style, 'rPr')),
            alignment: read_alignment(direct_word_child(style, 'pPr')),
        };

        if (
            style_type === 'paragraph'
            && (
                word_attribute(style, 'default') === '1'
                || name.toLocaleLowerCase('ru') === 'normal'
            )
        ) {
            default_style_id = style_id;
        }
    }

    const default_run_properties = first_word_element(
        first_word_element(document_xml, 'rPrDefault'),
        'rPr'
    );
    const default_paragraph_properties = first_word_element(
        first_word_element(document_xml, 'pPrDefault'),
        'pPr'
    );

    return {
        styles,
        default_style_id,
        default_font: read_font(default_run_properties),
        default_alignment: read_alignment(default_paragraph_properties),
    };
};

const resolve_style_value = (
    style_id,
    property_name,
    style_data,
    visited = new Set()
) => {
    if (!style_id || visited.has(style_id)) {
        return '';
    }

    visited.add(style_id);
    const style = style_data.styles[style_id] || {};

    return style[property_name]
        || resolve_style_value(
            style.based_on,
            property_name,
            style_data,
            visited
        );
};

const ensure_run_font = (document_xml, run, font_name) => {
    if (!font_name) {
        return;
    }

    let run_properties = direct_word_child(run, 'rPr');

    if (!run_properties) {
        run_properties = create_word_element(document_xml, 'rPr');
        run.insertBefore(run_properties, run.firstChild);
    }

    if (direct_word_child(run_properties, 'rFonts')) {
        return;
    }

    const fonts = create_word_element(document_xml, 'rFonts');

    for (const attribute of ['ascii', 'hAnsi', 'eastAsia', 'cs']) {
        fonts.setAttributeNS(
            word_namespace,
            `w:${attribute}`,
            font_name
        );
    }

    run_properties.insertBefore(fonts, run_properties.firstChild);
};

const ensure_paragraph_alignment = (
    document_xml,
    paragraph,
    alignment
) => {
    if (!alignment) {
        return;
    }

    let paragraph_properties = direct_word_child(paragraph, 'pPr');

    if (!paragraph_properties) {
        paragraph_properties = create_word_element(document_xml, 'pPr');
        paragraph.insertBefore(paragraph_properties, paragraph.firstChild);
    }

    if (direct_word_child(paragraph_properties, 'jc')) {
        return;
    }

    const alignment_element = create_word_element(document_xml, 'jc');
    alignment_element.setAttributeNS(
        word_namespace,
        'w:val',
        alignment
    );
    paragraph_properties.appendChild(alignment_element);
};

const normalize_word_xml = (xml_text, style_data) => {
    const document_xml = new DOMParser().parseFromString(
        xml_text,
        'application/xml'
    );
    const default_font = resolve_style_value(
        style_data.default_style_id,
        'font',
        style_data
    ) || style_data.default_font;
    const default_alignment = resolve_style_value(
        style_data.default_style_id,
        'alignment',
        style_data
    ) || style_data.default_alignment;

    for (const paragraph of document_xml.getElementsByTagNameNS(
        word_namespace,
        'p'
    )) {
        const paragraph_properties = direct_word_child(paragraph, 'pPr');
        const paragraph_style_id = word_attribute(
            direct_word_child(paragraph_properties, 'pStyle'),
            'val'
        ) || style_data.default_style_id;
        const paragraph_font = resolve_style_value(
            paragraph_style_id,
            'font',
            style_data
        ) || default_font;
        const paragraph_alignment = resolve_style_value(
            paragraph_style_id,
            'alignment',
            style_data
        ) || default_alignment;
        ensure_paragraph_alignment(
            document_xml,
            paragraph,
            paragraph_alignment
        );

        for (const run of paragraph.getElementsByTagNameNS(
            word_namespace,
            'r'
        )) {
            const run_properties = direct_word_child(run, 'rPr');
            const run_style_id = word_attribute(
                direct_word_child(run_properties, 'rStyle'),
                'val'
            );
            const run_font = resolve_style_value(
                run_style_id,
                'font',
                style_data
            ) || paragraph_font;
            ensure_run_font(document_xml, run, run_font);
        }
    }

    return new XMLSerializer().serializeToString(document_xml);
};


export const normalize_docx_for_preview = async (blob) => {
    const archive = await JSZip.loadAsync(blob);
    const styles_file = archive.file('word/styles.xml');

    if (!styles_file) {
        return blob;
    }

    const style_data = parse_styles(await styles_file.async('string'));
    const word_files = Object.keys(archive.files).filter(
        (file_name) => word_files_pattern.test(file_name)
    );

    for (const file_name of word_files) {
        const file = archive.file(file_name);

        if (!file) {
            continue;
        }

        const xml_text = await file.async('string');
        archive.file(
            file_name,
            normalize_word_xml(xml_text, style_data)
        );
    }

    return archive.generateAsync({
        type: 'blob',
        mimeType: blob.type
            || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
};
